import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePasswordResetViewModel } from "@/viewModels/usePasswordResetViewModel";
import { ResetPasswordForm } from "@/ui/components/ResetPasswordForm";
import { AuthLayout } from "@/ui/layouts";
import type { ResetPasswordFormData } from "@/schemas/passwordResetSchemas";

/**
 * Reset Password Page
 *
 * Pure page component following MVVM pattern.
 * - No business logic (all in ViewModel)
 * - Single useEffect for lifecycle (onMount)
 * - Delegates all user actions to ViewModel
 *
 * Story: 3.3.b - Password Reset Frontend UI
 *
 * Features:
 * - Verification code input
 * - New password input with strength indicator
 * - Confirm password validation
 * - Resend code functionality with cooldown
 * - Redirects to login on success
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const { state, actions, computed } = usePasswordResetViewModel();

  // Preserve state from ForgotPasswordPage (email and cooldown)
  // Clear errors only on unmount (empty dependency array)
  useEffect(() => {
    return () => {
      actions.clearError();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array = cleanup only runs on unmount

  // If no email stored, shouldn't be on this page
  // Don't show error guard during success status
  // (success toast shows + navigation happens after delay)
  // @ts-expect-error - TODO: Add status property to PasswordResetViewModelState interface
  if (!computed.showResetForm && state.status !== "success") {
    return (
      <AuthLayout>
        <div className="text-center">
          <p className="text-[0.875rem] text-gray-600">
            {t("passwordReset.errors.unknownError")}
          </p>
        </div>
      </AuthLayout>
    );
  }

  // Pure view - delegate form submission to ViewModel
  const handleSubmit = async (formData: ResetPasswordFormData) => {
    await actions.resetPassword(formData.code, formData.newPassword);
  };

  const handleResendCode = async () => {
    await actions.resendCode();
  };

  return (
    <AuthLayout>
        <ResetPasswordForm
          email={state.email}
          onSubmit={handleSubmit}
          onResendCode={handleResendCode}
          isLoading={state.isResetting}
          canResend={computed.canResend}
          resendCooldown={state.resendCooldown}
          error={computed.hasError ? computed.errorMessage : null}
        />
      </AuthLayout>
  );
}
